import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Search, Send, Check, Mail, MessageSquare, Save } from "lucide-react";
import { SOCIAL_PLATFORMS, SocialFields, SocialIcon, socialText, OpenLink } from "@/lib/socials";

const copy = (text, what) => navigator.clipboard.writeText(text).then(() => toast.success(`${what} copied`), () => toast.error("Couldn't copy — check browser permissions"));
const pill = (on) => `flex items-center gap-2 px-4 py-2 rounded-full border text-[11px] uppercase tracking-[0.15em] transition-colors ${on ? "border-gold/60 text-gold bg-gold/10" : "border-white/10 text-text-muted hover:text-text-main"}`;

const BrandLinks = () => {
  const [links, setLinks] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/socials").then(({ data }) => setLinks(data.brand)); }, []);
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/socials", { links }); setLinks(data.brand); toast.success("Velluto links saved"); }
    catch { toast.error("Couldn't save links"); }
    setSaving(false);
  };
  return (
    <section data-testid="brand-socials" className="rounded-3xl border border-white/8 bg-surface/60 p-8">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Velluto's accounts</p>
          <h3 className="font-display text-3xl mt-1">Brand social links</h3>
        </div>
        <div className="flex gap-2">
          <button data-testid="brand-socials-copy" onClick={() => copy(socialText(links) || "No links yet", "Velluto links")} className={pill(false)}><Copy size={13} /> Copy all</button>
          <button data-testid="brand-socials-save" onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2 rounded-full bg-wine hover:bg-wine-hover text-[11px] uppercase tracking-[0.15em] transition-colors disabled:opacity-50"><Save size={13} /> {saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
      <SocialFields prefix="brand-social" value={links} onChange={setLinks} showLinks />
    </section>
  );
};

const MemberRow = ({ m, selected, onToggle }) => {
  const filled = SOCIAL_PLATFORMS.filter((p) => m.socials?.[p.key]);
  return (
    <div data-testid={`social-member-${m.id}`} className={`flex items-start gap-4 p-4 rounded-2xl border transition-colors ${selected ? "border-gold/40 bg-gold/5" : "border-white/8"}`}>
      <button data-testid={`social-member-select-${m.id}`} onClick={onToggle} className={`mt-1 w-5 h-5 shrink-0 rounded border flex items-center justify-center ${selected ? "border-gold bg-gold text-ink" : "border-white/25"}`}>{selected && <Check size={12} />}</button>
      <img src={m.photo_url || "https://images.unsplash.com/photo-1637059880830-59a90102de77"} alt="" className="w-11 h-11 rounded-full object-cover shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm">{m.display_name}</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs">
          {m.address && <button data-testid={`copy-address-${m.id}`} onClick={() => copy(m.address, "Address")} className="text-gold hover:underline flex items-center gap-1">{m.address} <Copy size={10} /></button>}
          {m.email && <button data-testid={`copy-email-${m.id}`} onClick={() => copy(m.email, "Email")} className="text-text-muted hover:text-text-main flex items-center gap-1 break-all">{m.email} <Copy size={10} /></button>}
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {filled.length ? filled.map((p) => (
            <span key={p.key} className="flex items-center gap-1">
              <button data-testid={`copy-social-${m.id}-${p.key}`} onClick={() => copy(m.socials[p.key], p.label)}
                className="flex items-center gap-1.5 text-[10px] px-2.5 py-1 rounded-full border border-white/10 text-text-muted hover:text-text-main"><SocialIcon k={p.key} size={11} /> {m.socials[p.key]}</button>
              <OpenLink k={p.key} value={m.socials[p.key]} testId={`open-social-${m.id}-${p.key}`} size={10} />
            </span>
          )) : <span className="text-[11px] text-text-muted/60">No socials added yet</span>}
        </div>
      </div>
      <button data-testid={`copy-all-${m.id}`} onClick={() => copy([m.display_name, m.address, m.email, socialText(m.socials)].filter(Boolean).join("\n"), "Details")}
        className="p-2 rounded-full border border-white/10 text-text-muted hover:text-gold shrink-0" title="Copy all details"><Copy size={13} /></button>
    </div>
  );
};

const ForwardPanel = ({ count, onSend, sending }) => {
  const [channels, setChannels] = useState(["message"]);
  const [include, setInclude] = useState(["brand", "address"]);
  const [note, setNote] = useState("");
  const flip = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  return (
    <div data-testid="forward-panel" className="rounded-3xl border border-white/8 bg-surface/60 p-6 space-y-5 md:sticky md:top-24">
      <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Forward to {count} selected</p>
      <div className="flex flex-wrap gap-2">
        <button data-testid="forward-channel-message" onClick={() => flip(channels, setChannels, "message")} className={pill(channels.includes("message"))}><MessageSquare size={13} /> In-app</button>
        <button data-testid="forward-channel-email" onClick={() => flip(channels, setChannels, "email")} className={pill(channels.includes("email"))}><Mail size={13} /> Email</button>
      </div>
      <div className="flex flex-wrap gap-2">
        {[["brand", "Velluto links"], ["address", "Their @velluto"], ["own", "Their socials"]].map(([k, l]) => (
          <button key={k} data-testid={`forward-include-${k}`} onClick={() => flip(include, setInclude, k)} className={pill(include.includes(k))}>{l}</button>
        ))}
      </div>
      <textarea data-testid="forward-note" value={note} onChange={(e) => setNote(e.target.value)} rows={4} maxLength={1000}
        placeholder="Add a personal note (optional)" className="w-full bg-transparent border border-white/10 rounded-2xl p-4 text-sm focus:border-gold outline-none" />
      <button data-testid="forward-send" disabled={!count || !channels.length || sending} onClick={() => (!channels.includes("email") || count < 2 || window.confirm(`Email ${count} members? Each one uses your monthly email allowance.`)) && onSend({ channels, include, note })}
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-wine hover:bg-wine-hover text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-40">
        <Send size={13} /> {sending ? "Sending…" : `Send to ${count}`}
      </button>
    </div>
  );
};

export const SocialHub = () => {
  const [q, setQ] = useState("");
  const [modelsOnly, setModelsOnly] = useState(true);
  const [members, setMembers] = useState([]);
  const [sel, setSel] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setSel([]);
    const t = setTimeout(() => api.get("/admin/socials/members", { params: { q, creators_only: modelsOnly } })
      .then(({ data }) => setMembers(data)).catch(() => toast.error("Couldn't load members")), 250);
    return () => clearTimeout(t);
  }, [q, modelsOnly]);

  const send = async (payload) => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/socials/forward", { user_ids: sel, ...payload });
      toast.success(`Sent · ${data.sent.message} in-app, ${data.sent.email} email`);
      setSel([]);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send"); }
    setSending(false);
  };

  return (
    <div data-testid="social-hub" className="space-y-10">
      <BrandLinks />
      <div className="grid md:grid-cols-[1fr_320px] gap-8 items-start">
        <section className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px] flex items-center gap-2 border-b border-white/15 focus-within:border-gold">
              <Search size={14} className="text-text-muted" />
              <input data-testid="social-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, @handle or email"
                className="flex-1 bg-transparent py-2.5 text-sm outline-none" />
            </div>
            <button data-testid="social-models-only" onClick={() => setModelsOnly(!modelsOnly)} className={pill(modelsOnly)}>Models only</button>
            <button data-testid="social-select-all" onClick={() => setSel(sel.length === members.length ? [] : members.map((m) => m.id))} className={pill(false)}>
              {sel.length === members.length && members.length ? "Clear" : "Select all"}
            </button>
          </div>
          {members.length === 0 && <p data-testid="social-empty" className="text-sm text-text-muted py-8">No members match.</p>}
          {members.map((m) => (
            <MemberRow key={m.id} m={m} selected={sel.includes(m.id)}
              onToggle={() => setSel(sel.includes(m.id) ? sel.filter((x) => x !== m.id) : [...sel, m.id])} />
          ))}
        </section>
        <ForwardPanel count={sel.length} onSend={send} sending={sending} />
      </div>
    </div>
  );
};
